import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { Button, Card, CardActionArea, IconButton, Menu, MenuItem, useMediaQuery } from "@mui/material";
import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";

import { SubNavBar } from "components/common/SubNavBar";
import { Typography } from "new-components/typography/Typography";
import { TabChoices } from "routing/constants";
import { tabToPath } from "routing/routeSpecs";
import { DetectorModes } from "types/createDetector";
import { Detector } from "types/detectors";
import { User } from "types/users";

import { ActionOptions, OptionInformation } from "./DetectorActionModal";

export interface DetectorSubHeaderProps {
  user: User;
  queryInputIsDisabled: boolean;
  handleDiscardChanges: () => void;
  detector: Detector;
  handleSaveChanges: () => void;
  queryHasChanged: () => boolean;
  openActionDialog: (arg: any) => void;
  handleMenuOpen: (event: React.MouseEvent<HTMLButtonElement>) => void;
  handleMenuClose: () => void;
  anchorEl: any;
  menuIsOpen: boolean;
}

const DISABLED_EDIT_QUERY_MODES = [
  DetectorModes.COUNT,
  DetectorModes.MULTICHOICE,
  DetectorModes.TEXT_RECOGNITION,
  DetectorModes.BOUNDING_BOX,
];

/* eslint-disable prefer-arrow-callback */
export const DetectorSubHeader = React.memo(function DetectorSubHeader(props: DetectorSubHeaderProps) {
  const {
    user,
    queryInputIsDisabled,
    handleDiscardChanges,
    detector,
    handleSaveChanges,
    queryHasChanged,
    openActionDialog,
    handleMenuOpen,
    handleMenuClose,
    anchorEl,
    menuIsOpen,
  } = props;

  const isLargeScreen = useMediaQuery("(min-width:480px)");
  const location = useLocation();

  // When the user arrived from the detector list, preserve its filter/sort
  // query string so the back button returns to the list in the same state.
  // The Detectors nav tab navigates without this, so it still resets filters.
  const detectorListSearch = (location.state as { detectorListSearch?: string } | null)?.detectorListSearch ?? "";

  const showDetectorName = useMemo(() => {
    if (!queryInputIsDisabled) {
      return isLargeScreen;
    }
    return true;
  }, [isLargeScreen, queryInputIsDisabled]);

  const actionOptions = useMemo(() => {
    let options = Object.values(ActionOptions);
    if (!user.show_reset_detector_option) {
      options = options.filter((option) => option !== ActionOptions.Reset);
    }
    // Removing Edit option for modes in DISABLED_EDIT_QUERY_MODES
    if (DISABLED_EDIT_QUERY_MODES.includes(detector.mode)) {
      options = options.filter((option) => option !== ActionOptions.Edit);
    }
    return options;
  }, [detector.mode, user]);

  const handleCopy = (event: any) => {
    event.preventDefault(); // prevent the standard copy
    navigator.clipboard.writeText(detector.name);
  };

  return (
    <SubNavBar>
      <div className="flex flex-row w-full justify-between items-center">
        {showDetectorName && (
          <div className="flex flex-row gap-4 items-center w-full overflow-hidden">
            <Link
              to={{ pathname: tabToPath(TabChoices.Detectors), search: detectorListSearch }}
              className="no-underline"
            >
              <IconButton aria-label="back" className="p-1 ml-1">
                <ArrowBackIosNewIcon className="text-white" />
              </IconButton>
            </Link>

            <Typography textTransform="uppercase" className="self-center" onCopy={handleCopy} noWrap>
              {detector.name}
            </Typography>
          </div>
        )}

        <div className="flex flex-row justify-between gap-2">
          {!queryInputIsDisabled ? (
            <>
              <Button
                className="rounded-sm self-center h-[30px] w-auto p-2 bg-primary-darker"
                onClick={handleDiscardChanges}
              >
                <Typography fontWeight="medium" textTransform="capitalize" align="center" className="text-white">
                  Discard Changes
                </Typography>
              </Button>
              <Button
                className="rounded-sm self-center h-[30px] w-auto p-2 bg-white disabled:bg-neutral-400"
                onClick={handleSaveChanges}
                disabled={!queryHasChanged()}
              >
                <Typography fontWeight="medium" textTransform="capitalize" align="center" className="text-primary-darker">
                  Save Changes
                </Typography>
              </Button>
            </>
          ) : (
            <Card className="test w-[48px] h-[30px] bg-white">
              <CardActionArea onClick={handleMenuOpen} className="w-full h-full flex items-center justify-center">
                <MoreHorizIcon />
              </CardActionArea>
              <Menu
                anchorEl={anchorEl}
                open={menuIsOpen}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{
                  className: "w-[330px] mt-[8px] bg-neutral-200",
                }}
                MenuListProps={{
                  className: "py-[5px]",
                }}
              >
                {actionOptions.map((option) =>
                  OptionInformation[option] ? (
                    <MenuItem key={option} onClick={() => openActionDialog(option)} className="py-[8px]">
                      <div className="flex flex-row w-full justify-between">
                        <Typography fontWeight="semibold">
                          {OptionInformation[option].menuText}
                        </Typography>
                        {OptionInformation[option].subtext ? (
                          <Typography>
                            {OptionInformation[option].subtext}
                          </Typography>
                        ) : undefined}
                      </div>
                    </MenuItem>
                  ) : undefined
                )}
              </Menu>
            </Card>
          )}
        </div>
      </div>
    </SubNavBar>
  );
});
